import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Tv, Phone, ShieldCheck, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CareLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState('mobile');    // 'mobile' | 'otp'
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (localStorage.getItem('care_token')) navigate('/care/home');
  }, [navigate]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(r => r - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const sendOtp = async (e) => {
    e?.preventDefault?.();
    const digits = mobile.replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) return toast.error('Enter a valid 10-digit mobile');
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/care/otp/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: digits })
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Failed to send OTP');
      toast.success(j.message || 'OTP sent');
      setStep('otp');
      setResendIn(60);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e?.preventDefault?.();
    if (otp.length !== 6) return toast.error('Enter the 6-digit OTP');
    setBusy(true);
    try {
      const digits = mobile.replace(/\D/g, '').slice(-10);
      const r = await fetch(`${API}/api/care/otp/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: digits, otp })
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Verification failed');
      localStorage.setItem('care_token', j.token);
      localStorage.setItem('care_mobile', digits);
      toast.success('Welcome back!');
      navigate('/care/home');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-indigo-500/30">
            <Tv className="w-7 h-7 text-white" />
          </div>
          <div className="ml-3 text-white">
            <div className="text-2xl font-bold tracking-tight">ANT TV</div>
            <div className="text-xs text-indigo-200">Subscriber Self-Care</div>
          </div>
        </div>

        <Card className="p-6 bg-white/95 backdrop-blur shadow-2xl border-0">
          {step === 'mobile' && (
            <form onSubmit={sendOtp} className="space-y-4">
              <div>
                <h1 className="text-xl font-semibold text-slate-800">Sign in with mobile</h1>
                <p className="text-sm text-slate-500 mt-1">We'll send a one-time code to your WhatsApp.</p>
              </div>
              <div>
                <Label htmlFor="mobile" className="text-slate-700">Registered mobile number</Label>
                <div className="mt-1 flex items-center gap-2">
                  <div className="px-3 py-2 rounded-md bg-slate-100 text-slate-600 text-sm font-medium">+91</div>
                  <Input
                    id="mobile" type="tel" inputMode="numeric" maxLength={10}
                    autoFocus placeholder="10-digit mobile"
                    value={mobile} onChange={e => setMobile(e.target.value.replace(/\D/g, ''))}
                    data-testid="care-mobile-input"
                  />
                </div>
              </div>
              <Button type="submit" disabled={busy || mobile.length !== 10}
                className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="care-send-otp-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Phone className="w-4 h-4 mr-2" />}
                Send OTP
              </Button>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={verifyOtp} className="space-y-4">
              <div>
                <button type="button" onClick={() => { setStep('mobile'); setOtp(''); }}
                  className="text-xs text-indigo-600 hover:underline mb-2">
                  ← Change mobile
                </button>
                <h1 className="text-xl font-semibold text-slate-800">Enter the 6-digit code</h1>
                <p className="text-sm text-slate-500 mt-1">Sent to +91 {mobile}</p>
              </div>
              <Input
                type="text" inputMode="numeric" maxLength={6}
                autoFocus placeholder="••••••"
                value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                className="text-center tracking-[0.6em] text-2xl font-semibold"
                data-testid="care-otp-input"
              />
              <Button type="submit" disabled={busy || otp.length !== 6}
                className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="care-verify-otp-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                Verify & Continue
              </Button>
              <div className="text-center">
                <button type="button" onClick={sendOtp} disabled={resendIn > 0}
                  className="text-sm text-slate-500 hover:text-indigo-600 disabled:opacity-40"
                  data-testid="care-resend-otp-btn">
                  {resendIn > 0 ? `Resend OTP in ${resendIn}s` : 'Resend OTP'}
                </button>
              </div>
            </form>
          )}
        </Card>

        <p className="text-center text-xs text-indigo-200/70 mt-4">
          Powered by ANT Broadband · Trouble signing in? Contact your local operator.
        </p>
      </div>
    </div>
  );
}
